import React, {useState, useEffect} from 'react';
import {Copyright, Header} from "../components";
import Typography from "@mui/material/Typography";
import {Paper, Box, Button, TextField, Grid, InputLabel, FormControl, Select, MenuItem} from "@material-ui/core";
import useStyles from "../assets/styles/useStyles";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router";
import usePUT from "../hooks/usePUT";
import useGET from "../hooks/useGET";

function EditCompany() {

    const [responsePut, sendPutRequest] = usePUT();
    const [formValues, setFormValues] = useState({
        id: '',
        name: '',
        siret: '',
        logo: '',
    });
    const [companyList, setCompanyList] = useState([]);
    const [companyEdited, setCompanyEdited] = useState(false);
    const [sentRequest, setSentRequest] = useState({id: '', name: '', siret: '', logo: ''});
    const [errors, setErrors] = useState({
        siret: false,
        doesntExist: false,
        id: false,
        name: false,
        messages: [],
    });

    const [responseGet, sendGetRequest] = useGET();

    const navigate = useNavigate();
    const reduxUser = useSelector(state => state.userReducer.user);

    const classes = useStyles();

    useEffect(() => {
        if (!reduxUser.roles?.includes('ROLE_MASTER')) {
            navigate("/")
        }
    }, [reduxUser.roles, navigate])

    // récupérer toutes les entreprises
    useEffect(() => {
        sendGetRequest({
            id: 'getCompanies',
            url: '/api/companies/getAllCompanies',
            headers: {
                Authorization: `Bearer ${reduxUser.token}`
            }
        })
    }, [sendGetRequest, reduxUser]);

    useEffect(() => {
        const response = responseGet

        if (!response) return;

        try {
            if (response.status < 200 || response.status >= 300) {
                errors.messages.push("Une erreur est survenue lors de la récupération des entreprises")
            } else {
                setCompanyList(response.data);
            }
        } catch (err) {
            console.log("error while retrieving companies: " + err)
        }

    }, [responseGet, companyEdited]);

    const submitHandler = async (event) => {
        event.preventDefault();
        setCompanyEdited(false)
        const errorValues = {siret: false, doesntExist: false, id: false, name: false, messages: []}

        if (!(formValues.siret.length === 14)) {
            errorValues.siret = true;
            errorValues.messages.push("Ce SIRET est invalide");
        }
        if (formValues.name.length < 3) {
            errorValues.name = true;
            errorValues.messages.push("Ce nom est trop court")
        }
        if(!(formValues.id)) {
            errorValues.id = true;
            errorValues.messages.push("Merci de sélectionner une entreprise.")
        }

        setErrors(errorValues);

        if(errorValues.messages.length > 0) {
            return;
        }

        setSentRequest({
            id: formValues.id,
            name: formValues.name,
            siret: formValues.siret,
            logo: formValues.logo,
        })

        await sendPutRequest({
            id: 'editCompany',
            url: '/api/companies/editCompany',
            data: {
                id: formValues.id,
                name: formValues.name,
                siret: formValues.siret,
                logo: formValues.logo,
            },
            headers: {Authorization: `Bearer ${reduxUser.token}`}
        })
    }

    useEffect(() => {
        const response = responsePut
        if (!response) return;

        const errorValues = {siret: false, doesntExist: false, id: false, name: false, messages: []}
        setCompanyEdited(false)

        try {
            if (response.status >= 200 && response.status < 300) {
                setCompanyEdited(true);

                // refreshing companies list
                sendGetRequest({
                    id: 'getCompanies',
                    url: '/api/companies/getAllCompanies',
                    headers: {
                        Authorization: `Bearer ${reduxUser.token}`
                    }
                })
            } else if (response.status === 409) {
                errorValues.siret = true
                errorValues.messages.push("Ce SIRET existe déjà dans la base de données")
            } else if (response.status === 404) {
                errorValues.id = true
                errorValues.messages.push("Cette entreprise n'existe pas dans la base de données")
            } else if (response.status === 500) {
                errorValues.messages.push("Une erreur est survenue lors de la mise à jour de l'entreprise.")
            }
        } catch (err) {
            errorValues.messages.push("Une erreur est survenue lors de la mise à jour de l'entreprise.")
        }
        setErrors(errorValues)
    }, [responsePut])

    const handleChange = (e) => {
        const {name, value} = e.target;

        setFormValues((prev) => ({...prev, [name]: value}));
    }

    const handleCompanySelect = (e) => {
        const selectedId = e.target.value;

        const selectedCompany = companyList.find(c => c.id === selectedId);
        if (selectedCompany) {
            setFormValues({
                id: selectedCompany.id,
                name: selectedCompany.name,
                siret: selectedCompany.siret,
                logo: selectedCompany.logo || "",
            });
        }
    }

    return (
        <Grid container direction="column">
            <Grid item>
                <Header/>
            </Grid>

            <Grid item>
                <Grid
                    container
                    justifyContent="center"
                    style={{marginTop: 50}}
                >
                    <Paper elevation={3} className={classes.paper}>
                        <Typography
                            component="h1"
                            variant="h3"
                            align="center"
                            gutterBottom
                        >
                            Mettre à jour une entreprise
                        </Typography>

                        {errors.messages.map((m, i) => (
                            <Typography id="error-info" key={i}
                                        style={{display: "block",
                                        marginBottom: "5px"}}
                                        className={classes.message} align="center">
                                {m}
                            </Typography>)
                        )}

                        {companyEdited && (<Typography id="company-updated" style={{marginBottom: "5px"}}
                                                       className={classes.confirm} align="center">
                            L'entreprise {sentRequest.name} (SIRET: {sentRequest.siret}) a bien été mise à jour.
                        </Typography>)}

                        <form
                            className={classes.form}
                            onSubmit={submitHandler}
                        >
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={12}>
                                    <FormControl fullWidth
                                                 variant="outlined"
                                                 required>
                                        <InputLabel id="select-company-label">Sélectionnez une entreprise</InputLabel>
                                        <Select
                                            labelId="select-company-label"
                                            value={formValues.id}
                                            onChange={handleCompanySelect}
                                            label="Sélectionnez une entreprise"
                                            error={errors.id}
                                        >
                                            {companyList
                                                .sort((a,b) => {
                                                        if (a.name > b.name) return 1;
                                                        if (b.name > a.name) return -1;
                                                        return 0})
                                                .map((company) => (
                                                <MenuItem key={company.id} value={company.id}>
                                                    {company.name} (SIRET: {company.siret})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        variant="outlined"
                                        disabled
                                        required
                                        fullWidth
                                        name="id"
                                        label="ID de l'entreprise"
                                        error={errors.id}
                                        value={formValues.id}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        name="name"
                                        label="Nom de l'entreprise"
                                        value={formValues.name}
                                        error={errors.name}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        variant="outlined"
                                        required
                                        fullWidth
                                        name="siret"
                                        label="SIRET"
                                        error={errors.siret}
                                        value={formValues.siret}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={formValues.logo ? 9 : 12}>
                                            <TextField
                                                variant="outlined"
                                                fullWidth
                                                name="logo"
                                                label="Lien de l'image du logo (facultatif)"
                                                value={formValues.logo}
                                                onChange={handleChange}
                                            />
                                        </Grid>

                                        {formValues.logo && (
                                            <Grid item xs={3}>
                                                <img
                                                    src={formValues.logo}
                                                    alt="Aperçu logo"
                                                    style={{
                                                        maxHeight: 60,
                                                        maxWidth: "100%",
                                                        objectFit: "contain",
                                                        border: "1px solid #ccc",
                                                        borderRadius: "4px",
                                                    }}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "";
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Box display="flex" justifyContent="center">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    className={classes.submit}
                                >
                                    Envoyer
                                </Button>
                            </Box>
                        </form>
                    </Paper>
                </Grid>
            </Grid>

            <Box mt={5}>
                <Copyright/>
            </Box>
        </Grid>
    );
}

export default EditCompany;
